import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import PacientePlanillaForm from '../components/profile/PacientePlanillaForm';
import { getPatientLinkLabel, mapPatientFromApi } from '../constants/childInterests';
import { ocultarContactoPlanilla } from '../constants/pacientePlanilla';
import { getAgendaCountry } from '../constants/agendaTherapyTypes';
import {
  agendaCitaTipoLabel,
  buildSlotMap,
  CALENDAR_HOURS,
  cancelAgendaCita,
  confirmAgendaCita,
  ensureAgendaTeamsLink,
  fetchAgendaChat,
  fetchAgendaPacienteFicha,
  fetchAvailability,
  fetchEspecialistaServicios,
  formatAgendaPrecio,
  formatDateYMD,
  formatSlotLabel,
  MENSAJE_PAGO_24H,
  getWeekRange,
  isAgendaSlotHoraActual,
  isAgendaSlotHoraSuperada,
  isAgendaSlotReenviarDisponible,
  isPastAgendaDate,
  isPastAgendaSlot,
  payAgendaCita,
  rescheduleAgendaCita,
  sendAgendaChatMessage,
} from '../services/agendaService';
import { getUserDisplayName, getUserSession } from '../services/authService';
import { fetchUserProfile, getChildAgeLabel, getChildGenderLabel } from '../services/profileService';
import '../styles/agenda.css';

const CalendarIcon = () => (
  <svg className="agenda-event-meta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M8 3v4M16 3v4M3 11h18" />
  </svg>
);

const ClockIcon = () => (
  <svg className="agenda-event-meta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="8" />
    <path d="M12 8v4l3 2" />
  </svg>
);

const AgendaTeamsOpenButton = ({ url, habilitado }) => {
  if (!url) {
    return null;
  }

  if (habilitado) {
    return (
      <a
        className="agenda-slot-pay-btn agenda-teams-open"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        Abrir sala Teams
      </a>
    );
  }

  return (
    <button type="button" className="agenda-slot-pay-btn agenda-teams-open" disabled>
      Abrir sala Teams
    </button>
  );
};

/** Impresora en celda: verde con reporte, rojo si ya pasó la hora y no hay reporte. */
const AgendaReportPrinterIcon = ({ estado }) => (
  <svg
    className={`agenda-slot-printer-icon agenda-slot-printer-icon--${estado || 'off'}`}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M7 3h10v4H7V3zm-3 6h16a2 2 0 0 1 2 2v6h-4v-3H8v3H4v-6a2 2 0 0 1 2-2zm3 8h10v5H7v-5z"
      fill="currentColor"
    />
  </svg>
);

/** Bombillo en celda: apagado sin mensajes, amarillo si hay al menos uno. */
const AgendaChatBulbIcon = ({ encendido }) => (
  <svg
    className={`agenda-slot-bulb-icon${encendido ? ' agenda-slot-bulb-icon--on' : ''}`}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M9 21h6M10 17h4M12 3a6 6 0 0 0-4 10.5V15a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-1.5A6 6 0 0 0 12 3z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

/** Hilo unificado: propios a la derecha, del otro a la izquierda; reportes en morado. */
const AgendaChatThread = ({ mensajes, miUsuarioId, miRol }) => {
  if (!mensajes || mensajes.length === 0) {
    return null;
  }

  const yo = Number(miUsuarioId) || 0;
  const soyEspecialista = miRol === 'especialista';
  const vistaPaciente = miRol === 'paciente';

  return (
    <ul className="agenda-chat-thread">
      {mensajes.map((item) => {
        const esMio = yo > 0 && Number(item.remitente_usuario_id) === yo;
        // Especialista: solo tipo reporte en morado. Paciente: todos los del especialista en morado.
        const enMorado = vistaPaciente
          ? !esMio
          : item.tipo === 'reporte';
        const etiqueta = esMio
          ? soyEspecialista
            ? 'Especialista'
            : 'Paciente'
          : soyEspecialista
            ? 'Paciente'
            : 'Especialista';
        const clases = [
          'agenda-chat-bubble',
          esMio ? 'agenda-chat-bubble--mine' : 'agenda-chat-bubble--theirs',
          enMorado ? 'agenda-chat-bubble--reporte' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <li key={item.id} className={clases}>
            <span className="agenda-chat-bubble-meta">
              {item.creado_en || ''}
              {` · ${etiqueta}`}
            </span>
            <p>{item.cuerpo}</p>
          </li>
        );
      })}
    </ul>
  );
};

const blockLabel = (origen) => {
  if (origen === 'terapias') {
    return 'Ocupado en Terapias';
  }

  if (origen === 'terapias_libre') {
    return 'Libre (fuera de horario)';
  }

  if (origen === 'terapias_no_labora') {
    return 'No labora';
  }

  if (origen === 'terapias_sin_horario') {
    return 'Sin horario';
  }

  if (origen === 'nomina') {
    return 'No disponible';
  }

  if (origen === 'hora_pasada') {
    return 'Hora pasada';
  }

  if (origen === 'beyond' || origen === 'reservado' || origen === 'paciente_hora') {
    return 'Reservado';
  }

  if (origen === 'paciente_mismo_dia') {
    // Legacy: la grilla ya no usa este origen; se mantiene por compatibilidad.
    return 'Disponible';
  }

  if (origen === 'beyond_impaga') {
    return 'Por pagar';
  }

  if (origen === 'beyond_pagada') {
    return 'Pagada';
  }

  return 'No disponible';
};

const isCreatedPatient = (patient) =>
  Boolean(
    patient &&
      String(patient.nombre || '').trim() &&
      String(patient.documentoNumero || '').trim()
  );

const desplazamientoSemana = (fecha) => {
  const partes = String(fecha || '').split('-');

  if (partes.length !== 3) {
    return 0;
  }

  const objetivo = new Date(Number(partes[0]), Number(partes[1]) - 1, Number(partes[2]));

  if (Number.isNaN(objetivo.getTime())) {
    return 0;
  }

  objetivo.setHours(0, 0, 0, 0);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  const lunesDe = (fechaBase) => {
    const copia = new Date(fechaBase);
    const dia = copia.getDay();
    copia.setDate(copia.getDate() - ((dia + 6) % 7));
    copia.setHours(0, 0, 0, 0);
    return copia;
  };

  const diferencia = lunesDe(objetivo).getTime() - lunesDe(hoy).getTime();

  return Math.round(diferencia / (7 * 24 * 60 * 60 * 1000));
};

const AgendaCalendarPage = () => {
  const history = useHistory();
  const location = useLocation();
  const sessionUser = getUserSession();
  const rawBooking = location.state || {};
  const bookingInfo = (() => {
    const soyEspecialista = Boolean(sessionUser && sessionUser.tipo === 'especialista');
    const userId = (sessionUser && sessionUser.id) || null;
    if (!soyEspecialista || !userId) {
      return rawBooking;
    }
    const specialist = rawBooking.specialist || {};
    if (specialist.id || specialist.cedula) {
      return rawBooking;
    }
    return {
      ...rawBooking,
      modality: rawBooking.modality || { id: 'online', label: 'Terapia Online' },
      specialist: {
        ...specialist,
        id: userId,
        label: getUserDisplayName(sessionUser) || specialist.label || 'Especialista',
      },
    };
  })();

  const [weekOffset, setWeekOffset] = useState(() => {
    const fecha = location.state && location.state.slotPagado && location.state.slotPagado.fecha;
    return desplazamientoSemana(fecha);
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availability, setAvailability] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [patientsError, setPatientsError] = useState('');
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [typeModalPaso, setTypeModalPaso] = useState('tipo');
  const [tipoElegido, setTipoElegido] = useState('');
  const [serviciosEspecialista, setServiciosEspecialista] = useState([]);
  const [serviciosCargando, setServiciosCargando] = useState(false);
  const [serviciosError, setServiciosError] = useState('');
  const [savingCita, setSavingCita] = useState(false);
  const [citaMessage, setCitaMessage] = useState('');
  const [citaError, setCitaError] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  const [citaEnCambio, setCitaEnCambio] = useState(null);
  const [chatModo, setChatModo] = useState('reporte');
  const [chatTexto, setChatTexto] = useState('');
  const [chatMensajes, setChatMensajes] = useState([]);
  const [chatTieneReporte, setChatTieneReporte] = useState(false);
  const [chatCargando, setChatCargando] = useState(false);
  const [chatGuardando, setChatGuardando] = useState(false);
  const [chatError, setChatError] = useState('');
  const [chatOk, setChatOk] = useState('');
  const [fichaAbierta, setFichaAbierta] = useState(false);
  const [fichaPaciente, setFichaPaciente] = useState(null);
  const [fichaCargando, setFichaCargando] = useState(false);
  const [fichaError, setFichaError] = useState('');
  const preferSlotRef = useRef(null);
  const availabilityRequestRef = useRef(0);
  const loadingCountRef = useRef(0);
  const skipSlotRestoreRef = useRef(false);
  const loadParamsRef = useRef({});

  const sessionUserIdStable = useMemo(() => {
    if (!sessionUser) {
      return null;
    }
    return sessionUser.id || null;
  }, [sessionUser]);

  const chatCitaIdStable = useMemo(() => {
    const tipo = sessionUser && sessionUser.tipo;
    const cita = selectedSlot && selectedSlot.cita;
    if (!cita || !cita.id || cita.pago_estado !== 'pagada') {
      return 0;
    }
    if (tipo === 'especialista' && cita.es_del_especialista) {
      return Number(cita.id) || 0;
    }
    if (tipo === 'paciente' && cita.es_mia) {
      return Number(cita.id) || 0;
    }
    return 0;
  }, [sessionUser, selectedSlot]);

  const fichaCitaIdStable = useMemo(() => {
    const cita = selectedSlot && selectedSlot.cita;
    if (!cita || !cita.id) {
      return 0;
    }
    if (sessionUser && sessionUser.tipo === 'especialista' && cita.es_del_especialista) {
      return Number(cita.id) || 0;
    }
    return 0;
  }, [sessionUser, selectedSlot]);

  useEffect(() => {
    setFichaAbierta(false);
    setFichaPaciente(null);
    setFichaError('');
    setFichaCargando(false);
  }, [chatCitaIdStable, fichaCitaIdStable]);

  const sessionUserTipoStable = useMemo(() => {
    if (!sessionUser) {
      return '';
    }
    return sessionUser.tipo || '';
  }, [sessionUser]);

  useEffect(() => {
    if (sessionUserTipoStable !== 'especialista' || !sessionUserIdStable) {
      return;
    }
    const raw = location.state || {};
    const specialist = raw.specialist || {};
    if (specialist.id || specialist.cedula) {
      return;
    }
    const actual = getUserSession();
    history.replace({
      pathname: '/agenda/calendario',
      search: location.search || '',
      state: {
        ...raw,
        modality: raw.modality || { id: 'online', label: 'Terapia Online' },
        specialist: {
          ...specialist,
          id: sessionUserIdStable,
          label: getUserDisplayName(actual) || specialist.label || 'Especialista',
        },
      },
    });
  }, [history, location.search, location.state, sessionUserIdStable, sessionUserTipoStable]);

  const weekRange = useMemo(() => getWeekRange(weekOffset), [weekOffset]);

  const weekTitle = useMemo(() => {
    const first = weekRange.days[0];
    const last = weekRange.days[weekRange.days.length - 1];

    if (first.monthLabel === last.monthLabel) {
      return `${first.dayNumber} – ${last.dayNumber} de ${first.monthLabel}`;
    }

    return `${first.dayNumber} ${first.monthLabel} – ${last.dayNumber} ${last.monthLabel}`;
  }, [weekRange]);

  const slotMap = useMemo(
    () => buildSlotMap(availability && availability.slots),
    [availability]
  );

  const selectedPatient = useMemo(
    () => patients.find((item) => String(item.id) === String(selectedPatientId)) || null,
    [patients, selectedPatientId]
  );

  const serviciosEvaluacion = useMemo(
    () => serviciosEspecialista.filter((item) => item.tipo_cita === 'evaluacion'),
    [serviciosEspecialista]
  );
  const serviciosTerapia = useMemo(
    () => serviciosEspecialista.filter((item) => item.tipo_cita === 'terapia'),
    [serviciosEspecialista]
  );
  const serviciosDelTipo = useMemo(() => {
    if (tipoElegido === 'evaluacion') {
      return serviciosEvaluacion;
    }
    if (tipoElegido === 'terapia') {
      return serviciosTerapia;
    }
    return [];
  }, [tipoElegido, serviciosEvaluacion, serviciosTerapia]);

  const loadAvailability = useCallback((options) => {
    const opts =
      options && typeof options === 'object' && !options.nativeEvent ? options : {};
    const params = loadParamsRef.current;

    if (!params.canLoadAvailability) {
      loadingCountRef.current = 0;
      setLoading(false);
      return;
    }

    if (opts.clearPrefer) {
      preferSlotRef.current = null;
    }

    if (opts.skipRestore) {
      skipSlotRestoreRef.current = true;
    }

    const requestId = availabilityRequestRef.current + 1;
    availabilityRequestRef.current = requestId;
    loadingCountRef.current += 1;
    setLoading(true);
    setError('');
    setSelectedSlot(null);

    const payload = params.isPresencial
      ? {
          modalidad: 'presencial',
          terapiaDocumento: params.terapiaDocumento,
          sede: params.sedeId,
          especialistaNombre: params.especialistaNombre,
          fechaInicio: params.fechaInicio,
          fechaFin: params.fechaFin,
          solicitanteId: params.sessionUserId,
          pacienteFichaId: params.selectedPatientId || null,
        }
      : {
          especialistaId: params.especialistaId,
          fechaInicio: params.fechaInicio,
          fechaFin: params.fechaFin,
          solicitanteId: params.sessionUserId,
          pacienteFichaId: params.selectedPatientId || null,
        };

    fetchAvailability(payload)
      .then((data) => {
        if (requestId !== availabilityRequestRef.current) {
          return;
        }

        setAvailability(data);

        if (skipSlotRestoreRef.current) {
          skipSlotRestoreRef.current = false;
          preferSlotRef.current = null;
          setSelectedSlot(null);
          return;
        }

        const prefer = preferSlotRef.current;
        preferSlotRef.current = null;
        const slots = data.slots || [];

        if (!prefer) {
          const firstMia = slots.find(
            (slot) => slot.cita && slot.cita.es_mia && !isPastAgendaDate(slot.fecha)
          );
          const firstAvailable = slots.find(
            (slot) => slot.disponible && !isPastAgendaSlot(slot.fecha, slot.hora)
          );
          setSelectedSlot(firstMia || firstAvailable || null);
          return;
        }

        const stillExists = slots.find(
          (slot) => slot.fecha === prefer.fecha && slot.hora === prefer.hora
        );
        setSelectedSlot(stillExists || null);
      })
      .catch((err) => {
        if (requestId !== availabilityRequestRef.current) {
          return;
        }

        skipSlotRestoreRef.current = false;
        const rawMsg = err.message || 'No se pudo cargar la disponibilidad.';
        const looksLikeInformesApi =
          /informes/i.test(rawMsg) || /acci[oó]n no v[aá]lida/i.test(rawMsg);
        setError(
          looksLikeInformesApi
            ? 'Falta subir agenda_calendario_api.php a /backend/ (el calendario ya no usa agenda_disponibilidad.php).'
            : rawMsg
        );
        setAvailability(null);
        setSelectedSlot(null);
      })
      .finally(() => {
        loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
        if (loadingCountRef.current === 0) {
          setLoading(false);
        }
      });
  }, []);

  useEffect(() => {
    const specialist = bookingInfo.specialist || null;
    const modality = bookingInfo.modality || null;
    const locationData = bookingInfo.location || null;
    const presencial = Boolean(modality && modality.id === 'presencial');
    const documento = (specialist && specialist.cedula) || null;
    const espId = (specialist && specialist.id) || null;
    const sede = (specialist && specialist.sede) || (locationData && locationData.id) || '';
    const userId = (sessionUser && sessionUser.id) || null;
    const canLoad = Boolean((presencial && documento) || (!presencial && espId));

    loadParamsRef.current = {
      canLoadAvailability: canLoad,
      isPresencial: presencial,
      terapiaDocumento: documento,
      sedeId: sede,
      especialistaNombre: (specialist && specialist.label) || '',
      especialistaId: espId,
      fechaInicio: weekRange.start,
      fechaFin: weekRange.end,
      sessionUserId: userId,
      selectedPatientId: selectedPatientId,
    };
  }, [location.state, weekRange.start, weekRange.end, selectedPatientId, sessionUser]);

  useEffect(() => {
    const esRepresentante = sessionUserTipoStable === 'paciente';

    if (!esRepresentante || !sessionUserIdStable) {
      setPatients([]);
      setSelectedPatientId('');
      setPatientsError('');
      return undefined;
    }

    setLoadingPatients(true);
    setPatientsError('');

    fetchUserProfile(sessionUserIdStable)
      .then((data) => {
        const list = (data.hijos || data.pacientes || [])
          .map(mapPatientFromApi)
          .filter(isCreatedPatient);
        setPatients(list);

        if (list.length === 1) {
          setSelectedPatientId(String(list[0].id));
        } else if (list.length > 1) {
          setSelectedPatientId('');
        } else {
          setSelectedPatientId('');
        }
      })
      .catch((requestError) => {
        setPatientsError(requestError.message || 'No se pudieron cargar tus pacientes.');
        setPatients([]);
        setSelectedPatientId('');
      })
      .finally(() => {
        setLoadingPatients(false);
      });

    return undefined;
  }, [sessionUserIdStable, sessionUserTipoStable]);

  useEffect(() => {
    if (!location.state) {
      return;
    }

    if (location.state.slotPagado) {
      preferSlotRef.current = location.state.slotPagado;
      setCitaMessage(
        location.state.pagoStripe
          ? 'El pago quedó registrado. La cita está pagada.'
          : 'El pago de prueba quedó registrado. La cita está pagada.'
      );
    }

    if (location.state.horarioTomado) {
      setCitaMessage('');
      setCitaError(location.state.horarioTomado);
    }
  }, [location.state]);

  const specialistIdStable =
    (bookingInfo.specialist && bookingInfo.specialist.id) || null;
  const specialistCedulaStable =
    (bookingInfo.specialist && bookingInfo.specialist.cedula) || '';
  const modalityIdStable = (bookingInfo.modality && bookingInfo.modality.id) || '';
  const locationIdStable = (bookingInfo.location && bookingInfo.location.id) || '';

  useEffect(() => {
    const presencial = modalityIdStable === 'presencial';
    const canLoad = Boolean(
      (presencial && specialistCedulaStable) || (!presencial && specialistIdStable)
    );

    if (!canLoad) {
      return;
    }

    loadAvailability();
  }, [
    loadAvailability,
    specialistIdStable,
    specialistCedulaStable,
    modalityIdStable,
    locationIdStable,
    weekRange.start,
    weekRange.end,
    sessionUserIdStable,
    selectedPatientId,
  ]);

  useEffect(() => {
    if (!typeModalOpen) {
      return undefined;
    }

    const specialist = bookingInfo.specialist || null;
    const modality = bookingInfo.modality || null;
    const specialty = bookingInfo.specialty || null;
    const presencial = Boolean(modality && modality.id === 'presencial');
    const documento = (specialist && specialist.cedula) || null;
    const espId = (specialist && specialist.id) || null;
    const espCodigo = (specialty && (specialty.codigo || specialty.id)) || '';

    let vigente = true;
    setTypeModalPaso('tipo');
    setTipoElegido('');
    setServiciosCargando(true);
    setServiciosError('');
    setServiciosEspecialista([]);

    fetchEspecialistaServicios({
      especialistaId: presencial ? null : espId,
      terapiaDocumento: presencial ? documento : '',
      especialidadCodigo: espCodigo,
    })
      .then((lista) => {
        if (vigente) {
          setServiciosEspecialista(Array.isArray(lista) ? lista : []);
        }
      })
      .catch((err) => {
        if (vigente) {
          setServiciosEspecialista([]);
          setServiciosError(err.message || 'No se pudieron cargar los servicios.');
        }
      })
      .finally(() => {
        if (vigente) {
          setServiciosCargando(false);
        }
      });

    return () => {
      vigente = false;
    };
  }, [typeModalOpen, bookingInfo.specialist, bookingInfo.modality, bookingInfo.specialty]);

  useEffect(() => {
    if (!typeModalOpen) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !savingCita) {
        setTypeModalOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [typeModalOpen, savingCita]);

  useEffect(() => {
    const citaId = Number(chatCitaIdStable) || 0;

    // Vaciar siempre al cambiar de cita: cada cita pagada tiene su propio hilo.
    setChatMensajes([]);
    setChatTieneReporte(false);
    setChatTexto('');
    setChatError('');
    setChatOk('');

    if (!sessionUserIdStable || !citaId) {
      setChatCargando(false);
      return undefined;
    }

    let vigente = true;
    const citaIdPedido = citaId;
    setChatCargando(true);

    fetchAgendaChat({
      solicitante_id: sessionUserIdStable,
      cita_id: citaIdPedido,
      filtro: 'todos',
    })
      .then((data) => {
        if (!vigente) {
          return;
        }
        // Defensa: ignorar respuesta si ya no corresponde a la cita abierta.
        if (data && data.cita_id && Number(data.cita_id) !== citaIdPedido) {
          return;
        }
        setChatMensajes((data && data.mensajes) || []);
        setChatTieneReporte(Boolean(data && data.tiene_reporte));
        // Al abrir el hilo se marca leído en servidor: apagar bombillo en la celda.
        setAvailability((prev) => {
          if (!prev || !Array.isArray(prev.slots)) {
            return prev;
          }
          return {
            ...prev,
            slots: prev.slots.map((slot) => {
              if (!slot.cita || Number(slot.cita.id) !== citaIdPedido) {
                return slot;
              }
              return {
                ...slot,
                cita: {
                  ...slot.cita,
                  tiene_mensajes: false,
                },
              };
            }),
          };
        });
      })
      .catch((err) => {
        if (!vigente) {
          return;
        }
        setChatMensajes([]);
        setChatTieneReporte(false);
        setChatError(err.message || 'No se pudo cargar la conversación.');
      })
      .finally(() => {
        if (vigente) {
          setChatCargando(false);
        }
      });

    return () => {
      vigente = false;
    };
  }, [sessionUserIdStable, chatCitaIdStable]);

  const sessionUserId = (sessionUser && sessionUser.id) || null;
  const isRepresentante = Boolean(sessionUser && sessionUser.tipo === 'paciente');
  const isEspecialista = Boolean(sessionUser && sessionUser.tipo === 'especialista');
  const specialistInfo = bookingInfo.specialist || null;
  const modalityInfo = bookingInfo.modality || null;
  const locationInfo = bookingInfo.location || null;
  const specialtyInfo = bookingInfo.specialty || null;
  const especialistaId = (specialistInfo && specialistInfo.id) || null;
  const isPresencial = Boolean(modalityInfo && modalityInfo.id === 'presencial');
  const terapiaDocumento = (specialistInfo && specialistInfo.cedula) || null;
  const sedeId =
    (specialistInfo && specialistInfo.sede) || (locationInfo && locationInfo.id) || '';
  const canLoadAvailability = Boolean(
    (isPresencial && terapiaDocumento) || (!isPresencial && especialistaId)
  );

  let citasDelEspecialista = [];
  if (isEspecialista && availability && Array.isArray(availability.slots)) {
    citasDelEspecialista = availability.slots
      .filter((slot) => slot.cita && slot.cita.es_del_especialista && slot.cita.pago_estado === 'pagada')
      .slice()
      .sort((a, b) => {
        const porFecha = String(a.fecha).localeCompare(String(b.fecha));
        if (porFecha !== 0) {
          return porFecha;
        }
        return String(a.hora).localeCompare(String(b.hora));
      });
  }

  const citaSeleccionada =
    selectedSlot && selectedSlot.cita && selectedSlot.cita.es_mia ? selectedSlot.cita : null;
  const citaDelEspecialista =
    selectedSlot && selectedSlot.cita && selectedSlot.cita.es_del_especialista
      ? selectedSlot.cita
      : null;
  // Día completo pasado (para pagar/cancelar citas ya creadas).
  const slotDiaPasado = selectedSlot ? isPastAgendaDate(selectedSlot.fecha) : false;
  // Hora ya cumplida de hoy (para no agendar ni cambiar a esa franja).
  const slotHoraPasada = selectedSlot
    ? isPastAgendaSlot(selectedSlot.fecha, selectedSlot.hora)
    : false;
  const salaTeamsHoraActual = Boolean(
    selectedSlot && isAgendaSlotHoraActual(selectedSlot.fecha, selectedSlot.hora)
  );
  const puedeReenviarEnlace = Boolean(
    selectedSlot && isAgendaSlotReenviarDisponible(selectedSlot.fecha, selectedSlot.hora)
  );
  const citaPagada = Boolean(citaSeleccionada && citaSeleccionada.pago_estado === 'pagada');
  const citaPorPagar = Boolean(citaSeleccionada && !citaPagada);

  const canConfirmCita = Boolean(
    selectedSlot &&
      selectedSlot.disponible &&
      !slotHoraPasada &&
      !citaSeleccionada &&
      !rescheduling &&
      selectedPatient &&
      !loadingPatients &&
      !savingCita
  );

  // Hueco libre (sin cita propia): mostrar Confirmar bajo la disponibilidad.
  const showConfirmHuecoLibre = Boolean(
    !isEspecialista &&
      selectedSlot &&
      selectedSlot.disponible &&
      !citaSeleccionada &&
      !citaDelEspecialista
  );

  const showAvisoElegirPaciente = Boolean(
    showConfirmHuecoLibre &&
      !canConfirmCita &&
      isRepresentante &&
      !loadingPatients &&
      !rescheduling &&
      !slotHoraPasada
  );

  // Solo pacientes: el botón Confirmar cita parpadea tras elegir hora, hasta confirmar.
  const resaltarConfirmarPaciente = Boolean(
    isRepresentante &&
      selectedSlot &&
      selectedSlot.disponible &&
      !slotHoraPasada &&
      !citaSeleccionada &&
      !rescheduling
  );

  const canPayCita = Boolean(citaPorPagar && !slotDiaPasado && !rescheduling && !savingCita);
  const hoyAgenda = formatDateYMD(new Date());
  const puedeCambiarFechaPaciente = !isRepresentante || (selectedSlot && selectedSlot.fecha > hoyAgenda);
  const canManagePaidCita = Boolean(
    citaPagada && !slotDiaPasado && !rescheduling && !savingCita && puedeCambiarFechaPaciente
  );

  const rememberSlot = (fecha, hora) => {
    preferSlotRef.current = { fecha, hora };
  };

  const handleCloseTypeModal = () => {
    if (savingCita) {
      return;
    }

    setTypeModalOpen(false);
    setTypeModalPaso('tipo');
    setTipoElegido('');
  };

  const textoPrecioServicio = (servicio) => {
    if (serviciosCargando) {
      return 'Consultando precio…';
    }

    const precio = formatAgendaPrecio(servicio && servicio.precio, servicio && servicio.moneda);
    return precio || 'Sin precio en inventario';
  };

  const handleConfirmCita = () => {
    if (!selectedSlot || slotHoraPasada || !selectedSlot.disponible) {
      return;
    }

    if (!selectedPatient) {
      setPatientsError(
        isRepresentante
          ? 'Debes seleccionar el paciente para confirmar la cita.'
          : 'Debes seleccionar un paciente para confirmar la cita.'
      );
      return;
    }

    setPatientsError('');
    setCitaError('');
    setTypeModalOpen(true);
  };

  const confirmarConServicio = (tipoCita, servicio) => {
    if (!selectedSlot || !selectedPatient || savingCita || !servicio) {
      return;
    }

    setSavingCita(true);
    setCitaError('');
    setCitaMessage('');

    const payload = {
      solicitante_id: sessionUserId,
      paciente_ficha_id: selectedPatient.id,
      paciente_nombre: selectedPatient.nombre,
      tipo_cita: tipoCita,
      inventario_servicio_id: servicio.id,
      fecha: selectedSlot.fecha,
      hora: selectedSlot.hora,
      especialidad_codigo:
        (specialtyInfo && (specialtyInfo.codigo || specialtyInfo.id)) || '',
      zona_codigo: (locationInfo && locationInfo.pais) || '',
      sede: isPresencial ? sedeId : '',
    };

    if (isPresencial) {
      payload.modalidad = 'presencial';
      payload.terapia_documento = terapiaDocumento;
    } else {
      payload.modalidad = 'online';
      payload.especialista_id = especialistaId;
    }

    confirmAgendaCita(payload)
      .then(() => {
        setTypeModalOpen(false);
        setTypeModalPaso('tipo');
        setTipoElegido('');
        setCitaMessage(MENSAJE_PAGO_24H);
        rememberSlot(selectedSlot.fecha, selectedSlot.hora);
        setRescheduling(false);
        setCitaEnCambio(null);
        loadAvailability();
      })
      .catch((err) => {
        setCitaError(err.message || 'No se pudo confirmar la cita.');
      })
      .finally(() => {
        setSavingCita(false);
      });
  };

  const handleChooseTipo = (tipoCita) => {
    if (!selectedSlot || !selectedPatient || savingCita || serviciosCargando) {
      return;
    }

    const lista =
      tipoCita === 'evaluacion'
        ? serviciosEvaluacion
        : tipoCita === 'terapia'
          ? serviciosTerapia
          : [];

    if (lista.length === 0) {
      setCitaError(
        tipoCita === 'evaluacion'
          ? 'Este especialista no tiene evaluaciones configuradas.'
          : 'Este especialista no tiene terapias configuradas.'
      );
      return;
    }

    setCitaError('');
    setTipoElegido(tipoCita);
    setTypeModalPaso('servicio');
  };

  const handleChooseServicio = (servicio) => {
    if (!tipoElegido || !servicio) {
      return;
    }

    confirmarConServicio(tipoElegido, servicio);
  };

  const handleVolverTipo = () => {
    if (savingCita) {
      return;
    }

    setTypeModalPaso('tipo');
    setTipoElegido('');
    setCitaError('');
  };

  const handlePayCita = () => {
    if (!canPayCita || !citaSeleccionada) {
      return;
    }

    setSavingCita(true);
    setCitaError('');

    payAgendaCita({
      solicitante_id: sessionUserId,
      cita_id: citaSeleccionada.id,
      sitio_url: window.location.origin,
    })
      .then((data) => {
        setCitaMessage(MENSAJE_PAGO_24H);

        if (data && data.pasarela_modo === 'externo' && data.pasarela_url) {
          window.location.assign(data.pasarela_url);
          return;
        }

        history.push({
          pathname: '/agenda/pago',
          state: {
            cita: {
              ...citaSeleccionada,
              fecha: selectedSlot.fecha,
              hora: selectedSlot.hora,
            },
            booking: bookingInfo,
            pacienteNombre: selectedPatient ? selectedPatient.nombre : '',
          },
        });
      })
      .catch((err) => {
        setCitaError(err.message || 'No se pudo iniciar el pago.');
        loadAvailability();
      })
      .finally(() => {
        setSavingCita(false);
      });
  };

  const handleStartReschedule = () => {
    if (!canManagePaidCita || !citaSeleccionada || !selectedSlot) {
      return;
    }

    setCitaEnCambio({
      ...citaSeleccionada,
      fecha: selectedSlot.fecha,
      hora: selectedSlot.hora,
    });
    setRescheduling(true);
    setCitaError('');
    setCitaMessage(
      isRepresentante
        ? 'Elige otra fecha disponible, posterior a hoy.'
        : 'Elige otro horario disponible para cambiar la fecha.'
    );
  };

  const handleSaveReschedule = () => {
    if (!rescheduling || !citaEnCambio || !selectedSlot || savingCita) {
      return;
    }

    if (isPastAgendaSlot(selectedSlot.fecha, selectedSlot.hora) || !selectedSlot.disponible) {
      setCitaError('Elige un horario disponible que no haya pasado.');
      return;
    }

    if (isRepresentante && (selectedSlot.fecha <= hoyAgenda || selectedSlot.fecha === citaEnCambio.fecha)) {
      setCitaError('Elige otra fecha disponible, posterior a hoy.');
      return;
    }

    setSavingCita(true);
    setCitaError('');

    rescheduleAgendaCita({
      solicitante_id: sessionUserId,
      cita_id: citaEnCambio.id,
      fecha: selectedSlot.fecha,
      hora: selectedSlot.hora,
    })
      .then(() => {
        setCitaMessage('La fecha de la cita quedó actualizada.');
        rememberSlot(selectedSlot.fecha, selectedSlot.hora);
        setRescheduling(false);
        setCitaEnCambio(null);
        loadAvailability();
      })
      .catch((err) => {
        setCitaError(err.message || 'No se pudo cambiar la fecha.');
      })
      .finally(() => {
        setSavingCita(false);
      });
  };

  const handleCancelCita = () => {
    if (!canPayCita || !citaSeleccionada) {
      return;
    }

    if (!window.confirm('¿Eliminar esta cita? El horario volverá a quedar disponible.')) {
      return;
    }

    const citaIdEliminada = citaSeleccionada.id;
    const fechaEliminada = selectedSlot ? selectedSlot.fecha : null;
    const horaEliminada = selectedSlot ? selectedSlot.hora : null;

    setSavingCita(true);
    setCitaError('');

    cancelAgendaCita({
      solicitante_id: sessionUserId,
      cita_id: citaIdEliminada,
    })
      .then(() => {
        preferSlotRef.current = null;
        skipSlotRestoreRef.current = true;

        // Quita la cita y libera el resto del día bloqueado por "ya tienes cita".
        setAvailability((prev) => {
          if (!prev || !Array.isArray(prev.slots)) {
            return prev;
          }

          return {
            ...prev,
            slots: prev.slots.map((slot) => {
              const esLaMismaHora =
                fechaEliminada &&
                horaEliminada &&
                slot.fecha === fechaEliminada &&
                slot.hora === horaEliminada;
              const esLaMismaCita =
                slot.cita && Number(slot.cita.id) === Number(citaIdEliminada);
              const bloqueoMismoDia =
                fechaEliminada &&
                slot.fecha === fechaEliminada &&
                !slot.cita &&
                (slot.origen_bloqueo === 'paciente_mismo_dia' ||
                  slot.origen_bloqueo === 'paciente_hora');

              if (!esLaMismaHora && !esLaMismaCita && !bloqueoMismoDia) {
                return slot;
              }

              return {
                ...slot,
                disponible: true,
                origen_bloqueo: null,
                terapias_estado: slot.terapias_estado,
                cita: null,
              };
            }),
          };
        });

        setCitaMessage('La cita fue eliminada.');
        setCitaError('');
        setSelectedSlot(null);
        setRescheduling(false);
        setCitaEnCambio(null);

        // Recarga forzada desde el servidor.
        loadAvailability({ clearPrefer: true, skipRestore: true });
      })
      .catch((err) => {
        setCitaError(err.message || 'No se pudo cancelar la cita.');
        // Si falló, sincroniza por si el estado cambió en el servidor.
        loadAvailability({ clearPrefer: true, skipRestore: true });
      })
      .finally(() => {
        setSavingCita(false);
      });
  };

  const handleSaveChat = () => {
    const citaActiva = citaDelEspecialista || citaSeleccionada;
    if (!citaActiva || !citaActiva.id || chatGuardando) {
      return;
    }

    const texto = String(chatTexto || '').trim();
    if (!texto) {
      setChatError(isEspecialista && chatModo === 'reporte'
        ? 'Escribe el reporte de la sesión.'
        : 'Escribe un mensaje.');
      setChatOk('');
      return;
    }

    const tipoEnvio = isEspecialista && chatModo === 'reporte' ? 'reporte' : 'normal';

    setChatGuardando(true);
    setChatError('');
    setChatOk('');

    const citaIdEnvio = Number(citaActiva.id);

    sendAgendaChatMessage({
      solicitante_id: sessionUserId,
      cita_id: citaIdEnvio,
      tipo: tipoEnvio,
      mensaje: texto,
      filtro: 'todos',
    })
      .then((data) => {
        if (data && data.cita_id && Number(data.cita_id) !== citaIdEnvio) {
          return;
        }
        setChatMensajes((data && data.mensajes) || []);
        setChatTieneReporte(Boolean(data && data.tiene_reporte));
        setChatTexto('');
        setChatOk(tipoEnvio === 'reporte' ? 'Reporte guardado.' : 'Mensaje enviado.');
        if (selectedSlot) {
          rememberSlot(selectedSlot.fecha, selectedSlot.hora);
        }
        loadAvailability();
      })
      .catch((err) => {
        setChatError(err.message || 'No se pudo guardar.');
      })
      .finally(() => {
        setChatGuardando(false);
      });
  };

  const handleOpenFichaPaciente = () => {
    if (!citaDelEspecialista || !citaDelEspecialista.id || fichaCargando) {
      return;
    }

    if (fichaAbierta) {
      setFichaAbierta(false);
      return;
    }

    setFichaAbierta(true);
    setFichaCargando(true);
    setFichaError('');
    setFichaPaciente(null);

    fetchAgendaPacienteFicha({
      solicitante_id: sessionUserId,
      cita_id: citaDelEspecialista.id,
    })
      .then((data) => {
        setFichaPaciente((data && data.ficha) || null);
      })
      .catch((err) => {
        setFichaError(err.message || 'No se pudo abrir la ficha del paciente.');
      })
      .finally(() => {
        setFichaCargando(false);
      });
  };

  const handleTeamsLink = (reenviar) => {
    const citaActiva = citaSeleccionada || citaDelEspecialista;

    if (!citaActiva || !citaActiva.id || savingCita) {
      return;
    }

    setSavingCita(true);
    setCitaError('');

    ensureAgendaTeamsLink({
      solicitante_id: sessionUserId,
      cita_id: citaActiva.id,
      reenviar: Boolean(reenviar),
    })
      .then(() => {
        setCitaMessage(
          reenviar
            ? 'Se reenvió el correo con el enlace de Teams.'
            : 'Enlace de Teams listo. Si había correo del paciente, también se envió.'
        );
        rememberSlot(selectedSlot.fecha, selectedSlot.hora);
        loadAvailability();
      })
      .catch((err) => {
        setCitaError(err.message || 'No se pudo gestionar el enlace de Teams.');
      })
      .finally(() => {
        setSavingCita(false);
      });
  };

  const handleSelectSlot = (slot) => {
    if (isPastAgendaSlot(slot.fecha, slot.hora) && slot.disponible && !slot.cita) {
      return;
    }

    if (rescheduling) {
      if (!slot.disponible || isPastAgendaSlot(slot.fecha, slot.hora)) {
        return;
      }

      if (isRepresentante && citaEnCambio && (slot.fecha <= hoyAgenda || slot.fecha === citaEnCambio.fecha)) {
        setCitaError('Elige otra fecha disponible, posterior a hoy.');
        return;
      }

      setSelectedSlot(slot);
      setCitaError('');
      return;
    }

    setSelectedSlot(slot);
    setCitaError('');

    if (
      slot.cita &&
      slot.cita.es_mia &&
      slot.cita.pago_estado !== 'pagada' &&
      !isPastAgendaSlot(slot.fecha, slot.hora)
    ) {
      setCitaMessage(MENSAJE_PAGO_24H);
      return;
    }

    setCitaMessage('');
  };

  if (!canLoadAvailability) {
    return (
      <div className="agenda-root agenda-root--calendar">
        <section className="agenda-calendar">
          <div className="agenda-calendar-shell">
            <div className="agenda-calendar-empty">
              <h1>Calendario</h1>
              <p>Selecciona un especialista en la pantalla anterior para ver su disponibilidad.</p>
              <Link to={isEspecialista ? '/' : '/agenda'} className="agenda-calendar-back">
                {isEspecialista ? '← Volver al inicio' : '← Volver a agendar'}
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="agenda-root agenda-root--calendar">
      <section className="agenda-calendar">
        <div className="agenda-calendar-shell">
          <header className="agenda-calendar-header">
            <div className="agenda-calendar-header-links">
              <Link to={isEspecialista ? '/' : '/agenda'} className="agenda-calendar-back agenda-calendar-back--header">
                {isEspecialista ? '← Volver al inicio' : '← Volver a agendar'}
              </Link>
            </div>
            <div className="agenda-calendar-header-main">
              <h1>Calendario</h1>
              <div className="agenda-calendar-controls">
                <span className="agenda-calendar-week-label">{weekTitle}</span>
                <div className="agenda-calendar-nav">
                  <button
                    type="button"
                    aria-label="Semana anterior"
                    onClick={() => setWeekOffset((value) => value - 1)}
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    aria-label="Semana actual"
                    onClick={() => setWeekOffset(0)}
                    disabled={weekOffset === 0}
                  >
                    Hoy
                  </button>
                  <button
                    type="button"
                    aria-label="Semana siguiente"
                    onClick={() => setWeekOffset((value) => value + 1)}
                  >
                    →
                  </button>
                </div>
              </div>
            </div>

            <p className="agenda-calendar-context">
              {bookingInfo.modality?.label ? `${bookingInfo.modality.label} · ` : ''}
              {bookingInfo.specialty ? `${bookingInfo.specialty.label} · ` : ''}
              <span className="agenda-calendar-context-especialista">
                {bookingInfo.specialist?.label || availability?.especialista_nombre}
                {isEspecialista ? (
                  <button
                    type="button"
                    className="agenda-calendar-ficha-link"
                    disabled={!citaDelEspecialista}
                    onClick={handleOpenFichaPaciente}
                  >
                    Ficha
                  </button>
                ) : null}
              </span>
              {selectedPatient?.nombre ? ` · Paciente: ${selectedPatient.nombre}` : ''}
              {selectedPatient?.documentoNumero ? ` (${selectedPatient.documentoNumero})` : ''}
              {bookingInfo.location?.paisLabel
                ? ` · ${bookingInfo.location.paisLabel}`
                : ''}
              {bookingInfo.location ? ` · ${bookingInfo.location.label}` : ''}
              {availability?.vinculado_terapias ? ' · Disponibilidad unificada con Terapias' : ''}
            </p>
          </header>

          {error ? (
            <div className="agenda-calendar-alert agenda-calendar-alert--error" role="alert">
              {error}
              <button type="button" onClick={() => loadAvailability()}>
                Reintentar
              </button>
            </div>
          ) : null}

          {availability?.terapias_error ? (
            <div className="agenda-calendar-alert agenda-calendar-alert--warning" role="status">
              No se pudo consultar Terapias. Se muestra solo la ocupación de citas online en Beyond.
            </div>
          ) : null}

          <div className="agenda-calendar-board">
            <div className="agenda-calendar-grid-wrap">
              {loading ? <div className="agenda-calendar-loading">Cargando disponibilidad…</div> : null}

              <div
                className={`agenda-calendar-grid agenda-calendar-grid--slots${
                  loading ? ' agenda-calendar-grid--loading' : ''
                }`}
              >
                <div className="agenda-calendar-corner">Hora</div>

                {weekRange.days.map((day) => (
                  <div key={day.key} className="agenda-calendar-day-head">
                    <span className="agenda-calendar-day-number">{day.dayNumber}</span>
                    <span>{day.label}</span>
                  </div>
                ))}

                {CALENDAR_HOURS.map((time) => (
                  <React.Fragment key={time}>
                    <div className="agenda-calendar-time">{time}</div>
                    {weekRange.days.map((day) => {
                      const slotKey = `${day.date}|${time}`;
                      const slot = slotMap[slotKey];

                      if (!slot) {
                        return (
                          <div
                            key={slotKey}
                            className="agenda-calendar-cell agenda-calendar-cell--empty"
                            aria-hidden="true"
                          />
                        );
                      }

                      const isSelected =
                        selectedSlot?.fecha === slot.fecha && selectedSlot?.hora === slot.hora;
                      const esMia = Boolean(slot.cita && slot.cita.es_mia);
                      const fechaPasada = isPastAgendaSlot(slot.fecha, slot.hora);

                      const citaEspecialistaPagada = Boolean(
                        slot.cita &&
                          slot.cita.es_del_especialista &&
                          slot.cita.pago_estado === 'pagada'
                      );

                      if (esMia || citaEspecialistaPagada) {
                        const pagada = slot.cita.pago_estado === 'pagada';
                        const texto = citaEspecialistaPagada
                          ? slot.cita.paciente_nombre || 'Paciente'
                          : pagada && slot.cita.paciente_nombre
                            ? slot.cita.paciente_nombre
                            : agendaCitaTipoLabel(slot.cita.tipo_cita);
                        const tieneMensajes = Boolean(pagada && slot.cita.tiene_mensajes);
                        const tieneReporte = Boolean(pagada && slot.cita.tiene_reporte);
                        const reportePendiente = Boolean(
                          pagada && !tieneReporte && isAgendaSlotHoraSuperada(slot.fecha, slot.hora)
                        );
                        const estadoReporte = tieneReporte
                          ? 'hecho'
                          : reportePendiente
                            ? 'pendiente'
                            : 'off';
                        const titulos = [];

                        if (pagada) {
                          titulos.push(
                            tieneMensajes ? 'Hay mensajes sin leer' : 'Sin mensajes pendientes'
                          );
                          if (tieneReporte) {
                            titulos.push('Reporte realizado');
                          } else if (reportePendiente) {
                            titulos.push('Reporte pendiente');
                          }
                        }

                        return (
                          <button
                            key={slotKey}
                            type="button"
                            className={`agenda-calendar-cell ${
                              pagada ? 'agenda-slot-mine-paid' : 'agenda-slot-mine-unpaid'
                            }${isSelected ? ' agenda-slot-selected' : ''}`}
                            onClick={() => handleSelectSlot(slot)}
                            title={titulos.length ? titulos.join(' · ') : undefined}
                          >
                            {pagada ? (
                              <span className="agenda-slot-status-icons" aria-hidden="true">
                                <span className="agenda-slot-bulb-wrap">
                                  <AgendaChatBulbIcon encendido={tieneMensajes} />
                                </span>
                                <span className="agenda-slot-printer-wrap">
                                  <AgendaReportPrinterIcon estado={estadoReporte} />
                                </span>
                              </span>
                            ) : null}
                            <span className="agenda-slot-cell-label">{texto}</span>
                          </button>
                        );
                      }

                      if (slot.disponible) {
                        if (fechaPasada) {
                          return (
                            <div
                              key={slotKey}
                              className="agenda-calendar-cell agenda-slot-past"
                              title="Hora pasada"
                            >
                              Hora pasada
                            </div>
                          );
                        }

                        return (
                          <button
                            key={slotKey}
                            type="button"
                            className={`agenda-calendar-cell agenda-slot-available${
                              isSelected ? ' agenda-slot-selected' : ''
                            }`}
                            onClick={() => handleSelectSlot(slot)}
                          >
                            Disponible
                          </button>
                        );
                      }

                      return (
                        <div
                          key={slotKey}
                          className={`agenda-calendar-cell agenda-slot-blocked agenda-slot-blocked--${slot.origen_bloqueo || 'unknown'}`}
                          title={isEspecialista && (slot.origen_bloqueo === 'beyond_impaga' || slot.origen_bloqueo === 'beyond')
                            ? 'Ocupado'
                            : blockLabel(slot.origen_bloqueo)}
                        >
                          <span>
                            {isEspecialista && (slot.origen_bloqueo === 'beyond_impaga' || slot.origen_bloqueo === 'beyond')
                              ? 'Ocupado'
                              : blockLabel(slot.origen_bloqueo)}
                          </span>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>

              <div className="agenda-calendar-legend" aria-hidden="true">
                <span className="agenda-legend-item agenda-legend-item--available">
                  Disponible (gris Terapias)
                </span>
                <span className="agenda-legend-item agenda-legend-item--terapias">
                  Ocupado Terapias
                </span>
                <span className="agenda-legend-item agenda-legend-item--libre">
                  Libre / fuera de horario
                </span>
                <span className="agenda-legend-item agenda-legend-item--beyond">Reservado</span>
                {isEspecialista ? null : (
                  <span className="agenda-legend-item agenda-legend-item--unpaid">Tu cita por pagar</span>
                )}
                <span className="agenda-legend-item agenda-legend-item--paid">
                  {isEspecialista ? 'Cita pagada' : 'Tu cita pagada'}
                </span>
              </div>
            </div>

            <aside className="agenda-event-detail">
              {isRepresentante ? (
                <div className="agenda-event-patient agenda-event-patient--under-title">
                  <label className="agenda-event-patient-field" htmlFor="agenda-calendar-patient">
                    <span>Paciente de la cita *</span>
                    {loadingPatients ? (
                      <p className="agenda-event-patient-hint">Cargando pacientes…</p>
                    ) : null}
                    {!loadingPatients && patients.length === 0 ? (
                      <p className="agenda-event-patient-hint agenda-event-patient-hint--warning">
                        Debes registrar al menos un paciente en tu perfil.{' '}
                        <Link to="/perfil/pacientes">Ir a Pacientes</Link>
                      </p>
                    ) : null}
                    {!loadingPatients && patients.length > 0 ? (
                      <select
                        id="agenda-calendar-patient"
                        className="agenda-booking-select"
                        value={selectedPatientId}
                        onChange={(event) => {
                          setSelectedPatientId(event.target.value);
                          setPatientsError('');
                        }}
                        required
                      >
                        {patients.length > 1 ? (
                          <option value="">Elige un paciente</option>
                        ) : null}
                        {patients.map((patient) => (
                          <option key={patient.id} value={String(patient.id)}>
                            {patient.nombre || 'Paciente'}
                            {patient.documentoNumero ? ` · ${patient.documentoNumero}` : ''}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </label>
                  {patientsError ? (
                    <p className="agenda-event-patient-hint agenda-event-patient-hint--error">
                      {patientsError}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {selectedSlot ? (
                <>
                  <h2>
                    {rescheduling
                      ? 'Nueva fecha'
                      : citaDelEspecialista
                        ? 'Paciente agendado'
                        : citaSeleccionada
                          ? 'Tu cita'
                          : 'Horario disponible'}
                  </h2>

                  <div className="agenda-event-detail-meta">
                    <p>
                      <CalendarIcon />
                      {formatSlotLabel(selectedSlot.fecha, selectedSlot.hora).split(' · ')[0]}
                    </p>
                    <p>
                      <ClockIcon />
                      {selectedSlot.hora}
                    </p>
                  </div>

                  <h3>Especialista</h3>
                  <p>{bookingInfo.specialist?.label || availability?.especialista_nombre}</p>

                  {bookingInfo.location ? (
                    <>
                      <h3>
                        {bookingInfo.modality?.id === 'presencial' ? 'Sede presencial' : 'Modalidad'}
                      </h3>
                      <p>
                        {bookingInfo.location.paisLabel
                          ? `${bookingInfo.location.paisLabel} · `
                          : ''}
                        {bookingInfo.location.label}
                      </p>
                    </>
                  ) : null}

                  {citaDelEspecialista ? (
                    <div className="agenda-cita-status">
                      <h3>Paciente</h3>
                      <p className="agenda-cita-patient-name">
                        <strong>{citaDelEspecialista.paciente_nombre || 'Paciente'}</strong>
                      </p>
                      {fichaAbierta ? (
                        <div className="agenda-paciente-ficha">
                          {fichaCargando ? (
                            <p className="agenda-event-patient-hint">Cargando ficha…</p>
                          ) : null}
                          {fichaError ? (
                            <p className="agenda-event-patient-hint agenda-event-patient-hint--error">
                              {fichaError}
                            </p>
                          ) : null}
                          {fichaPaciente ? (
                            fichaPaciente.planilla && Array.isArray(fichaPaciente.planilla.secciones) ? (
                              <PacientePlanillaForm
                                planilla={ocultarContactoPlanilla(fichaPaciente.planilla)}
                                modo="lectura"
                                ocultarContacto
                              />
                            ) : (
                              <dl className="agenda-paciente-ficha-dl">
                                <div>
                                  <dt>Nombre</dt>
                                  <dd>{fichaPaciente.nombre || '—'}</dd>
                                </div>
                                <div>
                                  <dt>Documento</dt>
                                  <dd>
                                    {fichaPaciente.documento_tipo === 'pasaporte'
                                      ? 'Pasaporte'
                                      : fichaPaciente.documento_tipo === 'otro'
                                        ? 'Otro'
                                        : 'Cédula'}
                                    {fichaPaciente.documento_numero
                                      ? ' ' + fichaPaciente.documento_numero
                                      : ''}
                                  </dd>
                                </div>
                                <div>
                                  <dt>País</dt>
                                  <dd>{(getAgendaCountry(fichaPaciente.pais) || {}).label || '—'}</dd>
                                </div>
                                <div>
                                  <dt>Nacimiento</dt>
                                  <dd>
                                    {fichaPaciente.fecha_nacimiento || '—'}
                                    {fichaPaciente.fecha_nacimiento
                                      ? ' · ' + getChildAgeLabel(fichaPaciente.fecha_nacimiento)
                                      : ''}
                                  </dd>
                                </div>
                                <div>
                                  <dt>Género</dt>
                                  <dd>{getChildGenderLabel(fichaPaciente.genero)}</dd>
                                </div>
                                <div>
                                  <dt>Vínculo</dt>
                                  <dd>{getPatientLinkLabel(fichaPaciente.vinculo)}</dd>
                                </div>
                              </dl>
                            )
                          ) : null}
                        </div>
                      ) : null}
                      <p>{agendaCitaTipoLabel(citaDelEspecialista.tipo_cita)}</p>
                      <p>
                        {citaDelEspecialista.pago_estado === 'pagada'
                          ? 'Cita pagada.'
                          : 'Pendiente de pago.'}
                      </p>
                      {citaDelEspecialista.pago_estado === 'pagada' ? (
                        <div className="agenda-teams-box">
                          <h3>Sala Microsoft Teams</h3>
                          {citaDelEspecialista.teams_join_url ? (
                            <>
                              <p>
                                {salaTeamsHoraActual
                                  ? 'Pulsa para abrir la sala e iniciar la sesión.'
                                  : 'La sala solo se puede abrir en el día y la hora de la cita.'}
                              </p>
                              <AgendaTeamsOpenButton
                                url={citaDelEspecialista.teams_join_url}
                                habilitado={salaTeamsHoraActual}
                              />
                              {citaDelEspecialista.teams_passcode ? (
                                <p className="agenda-event-patient-hint">
                                  Código: <strong>{citaDelEspecialista.teams_passcode}</strong>
                                </p>
                              ) : null}
                              {puedeReenviarEnlace ? (
                                <button
                                  type="button"
                                  className="agenda-slot-secondary-btn"
                                  onClick={() => handleTeamsLink(true)}
                                  disabled={savingCita}
                                >
                                  Reenviar enlace al paciente
                                </button>
                              ) : null}
                            </>
                          ) : (
                            <>
                              <p>
                                Aún no hay enlace. Genéralo para anclarlo a esta cita y enviarlo al
                                paciente.
                              </p>
                              <button
                                type="button"
                                className="agenda-slot-pay-btn"
                                onClick={() => handleTeamsLink(false)}
                                disabled={savingCita}
                              >
                                {savingCita ? 'Creando sala…' : 'Crear enlace Teams'}
                              </button>
                            </>
                          )}
                        </div>
                      ) : null}

                      {citaDelEspecialista.pago_estado === 'pagada' ? (
                        <div className="agenda-report-box">
                          <div className="agenda-chat-modo" role="radiogroup" aria-label="Tipo de nota">
                            <label className="agenda-chat-modo-option">
                              <input
                                type="radio"
                                name="agenda-chat-modo"
                                value="reporte"
                                checked={chatModo === 'reporte'}
                                onChange={() => {
                                  setChatModo('reporte');
                                  setChatTexto('');
                                  setChatError('');
                                  setChatOk('');
                                }}
                              />
                              <span>Reporte</span>
                            </label>
                            <label className="agenda-chat-modo-option">
                              <input
                                type="radio"
                                name="agenda-chat-modo"
                                value="mensaje"
                                checked={chatModo === 'mensaje'}
                                onChange={() => {
                                  setChatModo('mensaje');
                                  setChatTexto('');
                                  setChatError('');
                                  setChatOk('');
                                }}
                              />
                              <span>Mensaje</span>
                            </label>
                            <Link
                              to={{
                                pathname: '/informes',
                                state: {
                                  booking: bookingInfo,
                                  estudioId: citaDelEspecialista.estudio_id || undefined,
                                },
                              }}
                              className="agenda-chat-modo-link"
                            >
                              Informe
                            </Link>
                          </div>

                          <h3>Mensajes de la cita</h3>
                          <p className="agenda-event-patient-hint">
                            {chatModo === 'mensaje'
                              ? 'Se enviará como mensaje.'
                              : 'Se guardará como reporte (morado).'}
                          </p>
                          {chatCargando ? (
                            <p className="agenda-event-patient-hint">Cargando…</p>
                          ) : null}
                          {chatMensajes.length > 0 ? (
                            <AgendaChatThread
                              key={`chat-esp-${citaDelEspecialista.id}`}
                              mensajes={chatMensajes}
                              miUsuarioId={sessionUserId}
                              miRol="especialista"
                            />
                          ) : !chatCargando ? (
                            <p className="agenda-event-patient-hint">
                              Aún no hay mensajes para esta cita.
                              {!(chatTieneReporte || citaDelEspecialista.tiene_reporte)
                                ? ' Tampoco hay reporte registrado.'
                                : ''}
                            </p>
                          ) : null}
                          <label className="agenda-report-field" htmlFor="agenda-chat-texto-esp">
                            <span>
                              {chatModo === 'mensaje'
                                ? 'Escribir mensaje'
                                : chatTieneReporte
                                  ? 'Agregar otro reporte'
                                  : 'Escribir reporte'}
                            </span>
                            <textarea
                              id="agenda-chat-texto-esp"
                              className="agenda-report-textarea"
                              rows={4}
                              value={chatTexto}
                              onChange={(event) => setChatTexto(event.target.value)}
                              placeholder={
                                chatModo === 'mensaje'
                                  ? 'Escribe un mensaje…'
                                  : 'Observaciones de la sesión…'
                              }
                              disabled={chatGuardando || chatCargando}
                            />
                          </label>
                          {chatError ? (
                            <p className="agenda-event-patient-hint agenda-event-patient-hint--error">
                              {chatError}
                            </p>
                          ) : null}
                          {chatOk ? (
                            <p className="agenda-cita-pay-notice" role="status">
                              {chatOk}
                            </p>
                          ) : null}
                          <button
                            type="button"
                            className="agenda-slot-pay-btn"
                            onClick={handleSaveChat}
                            disabled={chatGuardando || chatCargando}
                          >
                            {chatGuardando ? 'Guardando…' : 'Guardar'}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {!citaDelEspecialista && !citaSeleccionada && availability?.vinculado_terapias ? (
                    <>
                      <h3>Disponibilidad</h3>
                      <p>
                        Coincide con Terapias: solo se ofrece cuando la celda está vacía en gris claro
                        (disponible). Las horas azules (LIBRE) y las citas verdes quedan bloqueadas.
                      </p>
                    </>
                  ) : !citaDelEspecialista && !citaSeleccionada ? (
                    <>
                      <h3>Disponibilidad</h3>
                      <p>Este horario está libre para agendar una cita online en Beyond.</p>
                    </>
                  ) : null}

                  {showConfirmHuecoLibre ? (
                    <div className="agenda-slot-actions agenda-slot-actions--confirm">
                      <button
                        type="button"
                        className={`agenda-slot-book-btn${
                          resaltarConfirmarPaciente ? ' agenda-slot-book-btn--pulse' : ''
                        }`}
                        onClick={rescheduling ? handleSaveReschedule : handleConfirmCita}
                        disabled={
                          rescheduling
                            ? savingCita || slotHoraPasada
                            : !canConfirmCita
                        }
                      >
                        {rescheduling ? 'Guardar fecha' : 'Confirmar cita'}
                      </button>
                    </div>
                  ) : null}

                  {showAvisoElegirPaciente ? (
                    <p className="agenda-event-patient-hint agenda-event-patient-hint--warning">
                      {patients.length === 0
                        ? 'Registra un paciente para poder confirmar la cita.'
                        : 'Selecciona un paciente arriba para poder confirmar la cita.'}
                    </p>
                  ) : null}

                  {!citaSeleccionada && !rescheduling && !isRepresentante && !isEspecialista ? (
                    <div className="agenda-event-patient">
                      <h3>Paciente de la cita</h3>
                      <p className="agenda-event-patient-hint">
                        La selección de paciente para administradores y especialistas se definirá más
                        adelante. Sin paciente no se puede confirmar la cita.
                      </p>
                    </div>
                  ) : null}

                  {citaSeleccionada ? (
                    <div className="agenda-cita-status">
                      <h3>{agendaCitaTipoLabel(citaSeleccionada.tipo_cita)}</h3>
                      {citaPagada && isRepresentante && citaSeleccionada.paciente_nombre ? (
                        <p className="agenda-cita-patient-name">
                          <strong>{citaSeleccionada.paciente_nombre}</strong>
                        </p>
                      ) : null}
                      {citaPagada ? (
                        <p>
                          {(bookingInfo.modality?.id || citaSeleccionada.modalidad || 'online') !==
                          'presencial'
                            ? 'Cita pagada en modalidad virtual para cambiar la fecha debe hacerlo con 24 horas de anticipación por esta plataforma.'
                            : 'Cita pagada.'}
                        </p>
                      ) : (
                        <p className="agenda-cita-pay-notice" role="status">
                          {MENSAJE_PAGO_24H}
                        </p>
                      )}
                      {formatAgendaPrecio(citaSeleccionada.precio, citaSeleccionada.moneda) ? (
                        <p>
                          Precio:{' '}
                          <strong>
                            {formatAgendaPrecio(citaSeleccionada.precio, citaSeleccionada.moneda)}
                          </strong>
                          {citaSeleccionada.servicio_nombre
                            ? ` · ${citaSeleccionada.servicio_nombre}`
                            : ''}
                        </p>
                      ) : null}
                      {citaPagada && (bookingInfo.modality?.id || 'online') !== 'presencial' ? (
                        <div className="agenda-teams-box">
                          <h3>Sala Microsoft Teams</h3>
                          {citaSeleccionada.teams_join_url ? (
                            <>
                              <p>
                                {salaTeamsHoraActual
                                  ? 'Enlace para conectarse. Si entras en esta hora, puedes esperar en la sala de espera.'
                                  : 'La sala solo se puede abrir en el día y la hora de la cita.'}
                              </p>
                              <AgendaTeamsOpenButton
                                url={citaSeleccionada.teams_join_url}
                                habilitado={salaTeamsHoraActual}
                              />
                              {citaSeleccionada.teams_passcode ? (
                                <p className="agenda-event-patient-hint">
                                  Código: <strong>{citaSeleccionada.teams_passcode}</strong>
                                </p>
                              ) : null}
                            </>
                          ) : (
                            <p className="agenda-event-patient-hint agenda-event-patient-hint--warning">
                              El enlace de Teams aún no está listo. El especialista puede generarlo
                              desde esta cita.
                            </p>
                          )}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {slotDiaPasado ? (
                    <p className="agenda-event-patient-hint agenda-event-patient-hint--warning">
                      Esta fecha ya pasó. No se puede confirmar, pagar, cambiar ni eliminar.
                    </p>
                  ) : null}

                  {!citaSeleccionada && slotHoraPasada && !slotDiaPasado ? (
                    <p className="agenda-event-patient-hint agenda-event-patient-hint--warning">
                      Esta hora ya pasó. Elige otra hora disponible.
                    </p>
                  ) : null}

                  {citaMessage && !(citaSeleccionada && !citaPagada) ? (
                    <p className="agenda-cita-pay-notice" role="status">
                      {citaMessage}
                    </p>
                  ) : null}

                  {citaError ? (
                    <p className="agenda-event-patient-hint agenda-event-patient-hint--error" role="alert">
                      {citaError}
                    </p>
                  ) : null}

                  {!isEspecialista && citaPagada && isRepresentante && citaSeleccionada && citaSeleccionada.id ? (
                    <div className="agenda-slot-actions">
                      <Link
                        to={{
                          pathname: `/agenda/recibo/${citaSeleccionada.id}`,
                          state: {
                            booking: bookingInfo,
                            slotPagado: {
                              fecha: selectedSlot ? selectedSlot.fecha : citaSeleccionada.fecha,
                              hora: selectedSlot ? selectedSlot.hora : citaSeleccionada.hora,
                            },
                          },
                        }}
                        className="agenda-slot-pay-btn agenda-receipt-open"
                      >
                        Ver recibo
                      </Link>
                    </div>
                  ) : null}

                  {/* Con cita propia: pagar / eliminar / cambiar fecha (Confirmar ya está arriba si el hueco está libre). */}
                  {!isEspecialista && citaSeleccionada ? (
                    <>
                      <div className="agenda-slot-actions">
                        <button
                          type="button"
                          className="agenda-slot-pay-btn"
                          onClick={handlePayCita}
                          disabled={!canPayCita}
                        >
                          Pagar
                        </button>
                      </div>

                      {canPayCita ? (
                        <div className="agenda-slot-actions">
                          <button
                            type="button"
                            className="agenda-slot-secondary-btn agenda-slot-secondary-btn--danger"
                            onClick={handleCancelCita}
                            disabled={savingCita}
                          >
                            Eliminar cita
                          </button>
                        </div>
                      ) : null}

                      {canManagePaidCita ? (
                        <div className="agenda-slot-actions">
                          <button
                            type="button"
                            className="agenda-slot-secondary-btn"
                            onClick={handleStartReschedule}
                            disabled={savingCita}
                          >
                            Cambiar fecha
                          </button>
                        </div>
                      ) : null}
                    </>
                  ) : null}

                  {!isEspecialista && rescheduling ? (
                    <button
                      type="button"
                      className="agenda-slot-secondary-btn"
                      onClick={() => {
                        setRescheduling(false);
                        setCitaEnCambio(null);
                        setCitaMessage('');
                      }}
                    >
                      Volver
                    </button>
                  ) : null}

                  {citaPagada && isRepresentante && citaSeleccionada ? (
                    <div className="agenda-report-box">
                      <h3>Mensajes de la cita</h3>
                      <p className="agenda-event-patient-hint">
                        Conversación de esta cita con el especialista.
                      </p>
                      {chatCargando ? (
                        <p className="agenda-event-patient-hint">Cargando…</p>
                      ) : null}
                      {chatMensajes.length > 0 ? (
                        <AgendaChatThread
                          key={`chat-pac-${citaSeleccionada.id}`}
                          mensajes={chatMensajes}
                          miUsuarioId={sessionUserId}
                          miRol="paciente"
                        />
                      ) : !chatCargando ? (
                        <p className="agenda-event-patient-hint">
                          Aún no hay mensajes para esta cita.
                        </p>
                      ) : null}
                      <label className="agenda-report-field" htmlFor="agenda-chat-texto-pac">
                        <span>
                          {chatMensajes.length > 0 ? 'Responder' : 'Escribir mensaje'}
                        </span>
                        <textarea
                          id="agenda-chat-texto-pac"
                          className="agenda-report-textarea"
                          rows={4}
                          value={chatTexto}
                          onChange={(event) => setChatTexto(event.target.value)}
                          placeholder="Escribe un mensaje…"
                          disabled={chatGuardando || chatCargando}
                        />
                      </label>
                      {chatError ? (
                        <p className="agenda-event-patient-hint agenda-event-patient-hint--error">
                          {chatError}
                        </p>
                      ) : null}
                      {chatOk ? (
                        <p className="agenda-cita-pay-notice" role="status">
                          {chatOk}
                        </p>
                      ) : null}
                      <button
                        type="button"
                        className="agenda-slot-pay-btn"
                        onClick={handleSaveChat}
                        disabled={chatGuardando || chatCargando}
                      >
                        {chatGuardando ? 'Guardando…' : 'Guardar'}
                      </button>
                    </div>
                  ) : null}

                </>
              ) : isEspecialista ? (
                <>
                  <h2>Pacientes de la semana</h2>
                  {citasDelEspecialista.length === 0 ? (
                    <p>Esta semana no tienes pacientes agendados.</p>
                  ) : (
                    <div className="agenda-specialist-cita-list">
                      {citasDelEspecialista.map((slot) => (
                        <button
                          key={`${slot.fecha}|${slot.hora}`}
                          type="button"
                          className="agenda-slot-secondary-btn"
                          onClick={() => handleSelectSlot(slot)}
                        >
                        {slot.cita.paciente_nombre || 'Paciente'}
                        {' · '}
                        {formatSlotLabel(slot.fecha, slot.hora)}
                        {' · Pagada'}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h2>Selecciona un horario</h2>
                  <p>
                    {isRepresentante
                      ? 'Elige el paciente arriba y luego haz clic en una celda disponible del calendario.'
                      : 'Haz clic en una celda disponible del calendario para ver el detalle del horario.'}
                  </p>
                </>
              )}
            </aside>
          </div>
        </div>
      </section>

      {typeModalOpen ? (
        <div
          className="agenda-review-modal-backdrop"
          onClick={handleCloseTypeModal}
          role="presentation"
        >
          <div
            className="agenda-cita-type-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="agenda-cita-type-title"
          >
            <div className="agenda-review-modal-header">
              <h2 id="agenda-cita-type-title">
                {typeModalPaso === 'servicio'
                  ? agendaCitaTipoLabel(tipoElegido)
                  : 'Tipo de cita'}
              </h2>
              <button
                type="button"
                className="agenda-review-modal-close"
                onClick={handleCloseTypeModal}
                disabled={savingCita}
              >
                Cerrar
              </button>
            </div>

            {typeModalPaso === 'tipo' ? (
              <>
                <p>Elige si esta cita es una evaluación o una terapia.</p>
                {serviciosCargando ? (
                  <p className="agenda-booking-message">Cargando servicios del especialista…</p>
                ) : null}
                {!serviciosCargando && serviciosError ? (
                  <p
                    className="agenda-event-patient-hint agenda-event-patient-hint--error"
                    role="alert"
                  >
                    {serviciosError}
                  </p>
                ) : null}
                {!serviciosCargando &&
                !serviciosError &&
                serviciosEspecialista.length === 0 ? (
                  <p
                    className="agenda-event-patient-hint agenda-event-patient-hint--error"
                    role="status"
                  >
                    Este especialista no tiene servicios configurados. No se puede agendar.
                  </p>
                ) : null}
                <div className="agenda-slot-actions">
                  <button
                    type="button"
                    className="agenda-slot-book-btn agenda-cita-type-btn"
                    onClick={() => handleChooseTipo('evaluacion')}
                    disabled={
                      savingCita ||
                      serviciosCargando ||
                      Boolean(serviciosError) ||
                      serviciosEvaluacion.length === 0
                    }
                  >
                    Evaluación
                    <span className="agenda-cita-type-price">
                      {serviciosCargando
                        ? 'Consultando…'
                        : serviciosEvaluacion.length === 0
                          ? 'Sin servicios'
                          : `${serviciosEvaluacion.length} ${
                              serviciosEvaluacion.length === 1 ? 'opción' : 'opciones'
                            }`}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="agenda-slot-pay-btn agenda-cita-type-btn"
                    onClick={() => handleChooseTipo('terapia')}
                    disabled={
                      savingCita ||
                      serviciosCargando ||
                      Boolean(serviciosError) ||
                      serviciosTerapia.length === 0
                    }
                  >
                    Terapia
                    <span className="agenda-cita-type-price">
                      {serviciosCargando
                        ? 'Consultando…'
                        : serviciosTerapia.length === 0
                          ? 'Sin servicios'
                          : `${serviciosTerapia.length} ${
                              serviciosTerapia.length === 1 ? 'opción' : 'opciones'
                            }`}
                    </span>
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>Elige el servicio. El precio es el del inventario.</p>
                <button
                  type="button"
                  className="agenda-review-modal-close agenda-cita-type-back"
                  onClick={handleVolverTipo}
                  disabled={savingCita}
                >
                  Volver
                </button>
                <div className="agenda-cita-servicio-list">
                  {serviciosDelTipo.map((servicio) => (
                    <button
                      key={servicio.id}
                      type="button"
                      className="agenda-slot-book-btn agenda-cita-type-btn"
                      onClick={() => handleChooseServicio(servicio)}
                      disabled={savingCita}
                    >
                      {servicio.nombre}
                      <span className="agenda-cita-type-price">
                        {textoPrecioServicio(servicio)}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {citaError ? (
              <p className="agenda-event-patient-hint agenda-event-patient-hint--error" role="alert">
                {citaError}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AgendaCalendarPage;
